<template>
   <div :class="getWrapClass">
    <Tabs
      type="editable-card"
      size="small"
      :animated="false"
      :hideAdd="true"
      :tabBarGutter="3"
      :activeKey="activeKeyRef"
      @change="handleChange"
      @edit="handleEdit"
    >
      <template v-for="item in getTabsState" :key="item.query ? item.fullPath : item.path">
        <TabPane :closable="!(item && item.meta && item.meta.affix)&&item.name!=='Analysis'">
          <template #tab>
            <TabContent  :tabItem="item"/>
          </template>
        </TabPane>
      </template>

      <template #rightExtra v-if="getShowRedo || getShowQuick">
        <TabRedo v-if="getShowRedo"/>
        <TabContent isExtra :tabItem="$route" v-if="getShowQuick"/>
        <FoldButton v-if="getShowFold"/>
      </template>
    </Tabs>
  </div>
</template>
<script lang="ts">
import type {RouteLocationNormalized, RouteMeta} from 'vue-router';
import {defineComponent, computed, unref, ref} from 'vue';
import {Tabs} from 'ant-design-vue';
import TabContent from './components/TabContent.vue';
import FoldButton from './components/FoldButton.vue';
import TabRedo from './components/TabRedo.vue';
import {useGo} from '@/hooks/web/usePage';
import {useMultipleTabStore} from '@/store/modules/multipleTab';
import {useUserStore} from '@/store/modules/user';
import {initAffixTabs, useTabsDrag} from './useMultipleTabs';
import {useDesign} from '@/hooks/web/useDesign';
import {useMultipleTabSetting} from '@/hooks/setting/useMultipleTabSetting';
import {listenerRouteChange} from '@/logics/mitt/routeChange';
import {useRouter} from 'vue-router';
import {keepMenus} from "@/layouts/default/setting/skeepMenus";
import {StorageUtils} from "xmgoing-utils";
import GoingUtils from "@/utils/goingUtils";
import {handleGlobalClick} from "@/components/Watermark/hooks/useWatermarkHooks";
import eventBus from "@/events/eventBus";

export default defineComponent({
  name: 'MultipleTabs',
  components: {
    TabRedo,
    FoldButton,
    Tabs,
    TabPane: Tabs.TabPane,
    TabContent,
  },
  setup() {
    const affixTextList = initAffixTabs();
    const activeKeyRef = ref('');

    useTabsDrag(affixTextList);
    const tabStore = useMultipleTabStore();
    const userStore = useUserStore();
    const router = useRouter();

    const {prefixCls} = useDesign('multiple-tabs');
    const go = useGo();
    const {getShowQuick, getShowRedo, getShowFold} = useMultipleTabSetting();
    const getTabsState = computed(() => {
      return tabStore.getTabList.filter((item)=>{
        let flag=true;
        keepMenus.menus.forEach((itemObj:any)=>{
          if((item.path+'').indexOf(itemObj.path)>=0){
            flag=false;
          }
        });
        if(flag===false){
          return false;
        }
        return !item.meta?.hideTab
      });
    });

    const unClose = computed(() => unref(getTabsState).length === 1);

    const getWrapClass = computed(() => {
      return [
        prefixCls,
        {
          [`${prefixCls}--hide-close`]: unref(unClose),
        },
      ];
    });

    function findTabByKey(activeKey: string) {
      return tabStore.getTabList.find(
        (item) =>
          item.fullPath === activeKey ||
          item.path === activeKey ||
          (item.query ? item.fullPath : item.path) === activeKey,
      );
    }

    /** 切页签时还原该页签绑定的菜单入口 */
    function restoreMenuEntryFromTab(tab: RouteLocationNormalized | undefined) {
      const entryId = (tab?.meta as any)?.menuEntryId;
      if (entryId == null || entryId === '') return;
      StorageUtils.addSessionItem('curMenuEntryId', entryId);
      eventBus.emit('menuEntryChange', { entryId, path: tab?.path });
    }

    listenerRouteChange((route) => {
      const {name} = route;
      // if (name === REDIRECT_NAME || !route || !userStore.getToken) {
      //   return;
      // }

      const {path, fullPath, meta = {}, query} = route;
      const {currentActiveMenu, hideTab} = meta as RouteMeta;
      const isHide = !hideTab ? null : currentActiveMenu;
      const p = isHide || fullPath || path;
      if (activeKeyRef.value !== p) {
        activeKeyRef.value = p as string;
      }

      // 专业流程待办，特殊处理
      if (name === 'flowBpmList-simplFlowDeal') {
        const pageFrom = query.pageFrom as string
        activeKeyRef.value = `/flowBpmList/proDeal/${pageFrom}`
      }
      // 内部邮件子路由不添加到外层标签页（内部邮件有自己的标签页系统）
      // const isEmailChildRoute = path.indexOf('/emailMainPage/') >= 0 && name !== 'EmailCompose';
      // if (isEmailChildRoute) {
      //   return;
      // }

      if (isHide) {
        const findParentRoute = router
          .getRoutes()
          .find((item) => item.path === currentActiveMenu);

        findParentRoute && tabStore.addTab(findParentRoute as unknown as RouteLocationNormalized);
      } else {
        tabStore.addTab(unref(route));
      }
    });

    function handleChange(activeKey: any) {
      activeKeyRef.value = activeKey;
      // 切页签：还原该页签打开时的菜单入口，再跳转
      restoreMenuEntryFromTab(findTabByKey(activeKey));
      go(activeKey, false);
      handleGlobalClick()
    }

    // Close the current tab
    function handleEdit(targetKey: string) {
      // Added operation to hide, currently on  ly use delete operation
      if (unref(unClose)) {
        return;
      }
      tabStore.closeTabByKey(targetKey, router,go,tabStore);
    }

    return {
      getWrapClass,
      handleEdit,
      handleChange,
      activeKeyRef,
      getTabsState,
      getShowQuick,
      getShowRedo,
      getShowFold,
    };
  },
});
</script>
<style lang="less">
@import './index.less';

.vben-layout-multiple-header.newTabs {
  --tab-header: 44px;

  .vben-multiple-tabs-content__extra-quick, .vben-multiple-tabs-content__extra-redo, .vben-multiple-tabs-content__extra-fold {
    height: var(--tab-header, 30px);
    line-height: var(--tab-header, 30px);
  }

  .ant-tabs.ant-tabs-card .ant-tabs-nav .ant-tabs-tab {
    padding: 0 30px 0 30px;


  }

  .vben-multiple-tabs .ant-tabs.ant-tabs-card .ant-tabs-nav {
    border-bottom: 1px solid #d9d9d9;
  }

  .vben-multiple-tabs .ant-tabs.ant-tabs-card .ant-tabs-nav > div:nth-child(1) .ant-tabs-tab {
    margin-right: 0px !important;
  }

  .vben-multiple-tabs .ant-tabs-tab:not(.ant-tabs-tab-active) {
    border: 1px solid #FFF !important;
  }

  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
    &:hover {
      background-color: var(--ant-primary-1, #f0f0f0)
    }
  }

  .vben-multiple-tabs .ant-tabs.ant-tabs-card .ant-tabs-nav {
    height: var(--tab-header, 30px);
  }

  .vben-multiple-tabs .ant-tabs.ant-tabs-card .ant-tabs-nav .ant-tabs-tab {
    height: calc(var(--tab-header, 30px) - 12px);
    position: relative;
    top: 10px;
    padding-right: 10px !important;
    border-radius: var(--baseBorderRaduis, 4px) var(--baseBorderRaduis, 4px) 0 0;
    transition: padding .3s cubic-bezier(.645, .045, .355, 1) !important;
    overflow: visible;

    .ant-tabs-tab-remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 14px !important;
      min-width: 14px;
      margin: 0 0 0 6px !important;
      overflow: visible;
    }

    &.ant-tabs-tab-active{
      /* -webkit-mask:url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANoAAAAkCAYAAADvhSSyAAAACXBIWXMAAAsTAAALEwEAmpwYAAABbUlEQVR4nO3d4U3CUBSG4beJA9QNcAJhAtnAOoGOwCaOoBvoCDgBOIHdQDbAH7cJMaHtxcJB6/skJ6Thptw/H+cSktOCn5kCc+AWKJtraaxqYA28A8/N9cktgE9ga1n/tBYcqDhw/QtQHfoh0gitgdkpbrzg/N8klvWbKruz5Xa0CbAi/R6TtDMjdbdOF5k3qzBk0j4VRwzazbC9SKN1nbMo9+j4QTo+SvpuA1z2LcoN2nbYXqRR681R7tFR0gAGTQpg0KQABk0KYNCkAAZNCmDQpAAGTQpg0KQABk0KYNCkAAZNCmDQpAAGTQpg0KQABk0KYNCkAAZNGq4kjTRolRM0x31L3Sb0TMLKCZqTiaVu9/QErW+oyAQHp0p9NqRBqnXbgq6glcATdjQpRw1ctb3ZFrQSeAQejr8fabRegbvcxXPScfHcDxCwrL9YK/Y0qILdgwSnpNHfHhWl4ZbAW/O6LEgplHRC/mEtBfgClkhxraFbr7gAAAAASUVORK5CYII=); */
      mask: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANoAAAAkCAYAAADvhSSyAAAACXBIWXMAAAsTAAALEwEAmpwYAAABbUlEQVR4nO3d4U3CUBSG4beJA9QNcAJhAtnAOoGOwCaOoBvoCDgBOIHdQDbAH7cJMaHtxcJB6/skJ6Thptw/H+cSktOCn5kCc+AWKJtraaxqYA28A8/N9cktgE9ga1n/tBYcqDhw/QtQHfoh0gitgdkpbrzg/N8klvWbKruz5Xa0CbAi/R6TtDMjdbdOF5k3qzBk0j4VRwzazbC9SKN1nbMo9+j4QTo+SvpuA1z2LcoN2nbYXqRR681R7tFR0gAGTQpg0KQABk0KYNCkAAZNCmDQpAAGTQpg0KQABk0KYNCkAAZNCmDQpAAGTQpg0KQABk0KYNCkAAZNGq4kjTRolRM0x31L3Sb0TMLKCZqTiaVu9/QErW+oyAQHp0p9NqRBqnXbgq6glcATdjQpRw1ctb3ZFrQSeAQejr8fabRegbvcxXPScfHcDxCwrL9YK/Y0qILdgwSnpNHfHhWl4ZbAW/O6LEgplHRC/mEtBfgClkhxraFbr7gAAAAASUVORK5CYII=);
      -webkit-mask-size: 100% 100%;
      mask-size: 100% 100%;
    }
  }

  .vben-multiple-tabs {
    min-height: var(--tab-header, 30px);
    height: var(--tab-header, 30px);
  }

  .ant-tabs {
    min-height: var(--tab-header, 30px);
    padding-right: 20px;
    padding-left: 20px;

    &.ant-tabs-small {
      height: var(--tab-header, 30px);
    }
  }
}
</style>
